import React, { useState, useEffect } from 'react';
import { supabaseClient } from './services/supabase';
import Header from './components/Layout/Header';
import HeroBanner from './components/Hero/HeroBanner';
import Seasonal from './components/Sections/Seasonal';
import Menu from './components/Sections/Menu';
import Kodawari from './components/Sections/Kodawari';
import StoreInfo from './components/Sections/StoreInfo';
import HolidayCalendar from './components/Sections/HolidayCalendar';
import ReservationSection from './components/Reservation/ReservationSection';
import ReservationManagement from './components/Reservation/ReservationManagement';
import Access from './components/Sections/Access';
import Contact from './components/Sections/Contact';
import Footer from './components/Layout/Footer';
import PrivacyPage from './components/PrivacyPage';
import SeasonalEffect from './components/Effects/SeasonalEffect';
import './App.css';

function isPrivacyRoute() {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname.trim();
  return /^\/privacy\/?$/i.test(p) || /^\/privacy\.html$/i.test(p);
}

function App() {
  const [storeConfig, setStoreConfig] = useState({
    businessHours: { start: 11, end: 22 },
    lastOrder: 21.5,
    seats: {
      counter: { count: 7, maxCapacity: 4 },
      table: { count: 3, maxCapacity: 4 }
    },
    reservationDuration: 90,
    advanceBookingMonths: 2,
    maxGuestsPerSlot: 6
  });
  const [holidays, setHolidays] = useState([]);
  const [reservationEnabled, setReservationEnabled] = useState(true);
  const [seasonalEnabled, setSeasonalEnabled] = useState(true);
  const [seasonalItems, setSeasonalItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isPrivacyRoute()) {
      setLoading(false);
      return;
    }
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [settingsResult, holidaysResult, seasonalResult] = await Promise.all([
        supabaseClient
          .from('store_settings')
          .select('setting_key, setting_value')
          .in('setting_key', ['business_hours', 'reservation_enabled', 'max_guests_per_slot']),
        supabaseClient
          .from('holidays')
          .select('holiday_date')
          .order('holiday_date', { ascending: true }),
        supabaseClient
          .from('seasonal_items')
          .select('*')
          .eq('is_visible', true)
          .order('display_order', { ascending: true })
      ]);

      if (settingsResult.data) {
        const settingsMap = {};
        settingsResult.data.forEach(row => { settingsMap[row.setting_key] = row.setting_value; });

        const hours = settingsMap['business_hours'];
        if (hours) {
          setStoreConfig(prev => ({
            ...prev,
            businessHours: {
              start: parseInt(hours.start) || 11,
              end: parseInt(hours.end) || 22
            },
            lastOrder: parseFloat(hours.last_order) || 21.5
          }));
        }

        const toggleVal = settingsMap['reservation_enabled'];
        if (toggleVal !== undefined) {
          setReservationEnabled(typeof toggleVal === 'boolean' ? toggleVal : toggleVal === true || toggleVal === 'true');
        }

        const maxGuests = settingsMap['max_guests_per_slot'];
        if (maxGuests) {
          setStoreConfig(prev => ({
            ...prev,
            maxGuestsPerSlot: maxGuests.max || 6
          }));
        }
      }

      if (holidaysResult.data) {
        setHolidays(holidaysResult.data.map(h => h.holiday_date));
      }

      const seasonalItems = seasonalResult.data || [];
      setSeasonalItems(seasonalItems);
      setSeasonalEnabled(seasonalItems.length > 0);

    } catch (error) {
      console.error('設定読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  };

  if (isPrivacyRoute()) {
    return <PrivacyPage />;
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <SeasonalEffect />
      <Header reservationEnabled={reservationEnabled} seasonalEnabled={seasonalEnabled} />
      <main>
        <HeroBanner />
        <Seasonal items={seasonalItems} />
        <Menu />
        <Kodawari />
        <StoreInfo />
        <HolidayCalendar holidays={holidays} />
        {reservationEnabled && (
          <>
            <ReservationSection 
              storeConfig={storeConfig} 
              holidays={holidays} 
            />
            <ReservationManagement />
          </>
        )}
        <Access />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;

